import { useMemo } from 'react'
import { CONDITIONAL_DISPLAY_RULES } from '../utils/patientFormConfig'

/**
 * Pure derivation of which patient-form fields are visible for the current props/state.
 *
 * Previously this lived in a mutable manager that was updated inside an effect and then
 * forced a re-render, so every keystroke rendered twice (the first pass with stale
 * visibility). Deriving it with useMemo keeps it a single pass and makes it testable.
 */
export function computePatientFormVisibility({
  patientDetails,
  makeAnonymousPatient = false,
  isForcingAnonymousPatient = false,
  moveEmailToMoreSettings = false,
  hidePatientNumber = false,
  isAllowedToAddUHID = false,
  isAllowedToAddImportId = false,
  hideRelationShipDetails = false,
  showAdminFields = false,
  compactEssentials = false,
  overrides = {},
} = {}) {
  const contactVisible = !makeAnonymousPatient
  // Clinic assistants get the leanest possible essentials: email lives in More details.
  const emailInMoreDetails = Boolean(moveEmailToMoreSettings) || Boolean(compactEssentials)
  const attendants = patientDetails?.attendants

  const fields = {
    mobile: contactVisible,
    email: contactVisible,
    alternativeMobiles: contactVisible,
    alternativeEmails: contactVisible,
    name: true,
    gender: true,
    age: true,
    address: true,
    city: true,
    pincode: true,
    note: true,
    referredBy: true,
    language: true,
    attendants: true,
    communicationToAttendant: CONDITIONAL_DISPLAY_RULES.showCommunicationToAttendantWhen(attendants),
    communicationToPref: Boolean(showAdminFields),
    uhid: Boolean(isAllowedToAddUHID),
    importId: Boolean(isAllowedToAddImportId),
    patientNumber: !hidePatientNumber,
    relationDropdown:
      !hideRelationShipDetails && CONDITIONAL_DISPLAY_RULES.showRelationDropdownWhen(patientDetails),
    anonymousPatientCheckbox:
      !isForcingAnonymousPatient &&
      CONDITIONAL_DISPLAY_RULES.showAnonymousCheckboxWhen(patientDetails),
    moreDetailsSection: true,
    ...overrides,
  }

  const isFieldVisible = (fieldName) => fields[fieldName] !== false

  return {
    fields,
    isFieldVisible,
    getVisibleFields: () => Object.keys(fields).filter((key) => fields[key] !== false),
    getHiddenFields: () => Object.keys(fields).filter((key) => fields[key] === false),
    shouldShowEmailInBasicInfo: fields.email && !emailInMoreDetails,
    shouldShowEmailInMoreDetails: fields.email && emailInMoreDetails,
    shouldShowAlternativeEmailsInBasicInfo: fields.alternativeEmails && !emailInMoreDetails,
    shouldShowAlternativeEmailsInMoreDetails: fields.alternativeEmails && emailInMoreDetails,
    shouldShowSearchDropdown: fields.mobile,
    shouldShowAnonymousCheckbox: fields.anonymousPatientCheckbox,
    shouldShowRelationDropdown: fields.relationDropdown,
  }
}

export const useFormFieldVisibility = ({
  patientDetails,
  makeAnonymousPatient,
  isForcingAnonymousPatient,
  moveEmailToMoreSettings,
  hidePatientNumber,
  isAllowedToAddUHID,
  isAllowedToAddImportId,
  hideRelationShipDetails,
  showAdminFields,
  compactEssentials,
  initialVisibilityConfig,
}) => {
  const attendantsCount = Array.isArray(patientDetails?.attendants)
    ? patientDetails.attendants.length
    : 0
  const relatedPatientKey = [
    patientDetails?.relatedToExistingPatient?.id,
    patientDetails?.relatedToExistingPatient?.patientId,
    patientDetails?.relatedToExistingPatient?.userId,
    patientDetails?.dependentRelation,
  ].join('|')
  const hasMobile = Boolean(patientDetails?.mobile)
  const hasEmail = Boolean(patientDetails?.email)
  const overridesKey = JSON.stringify(initialVisibilityConfig || {})

  return useMemo(
    () =>
      computePatientFormVisibility({
        patientDetails,
        makeAnonymousPatient,
        isForcingAnonymousPatient,
        moveEmailToMoreSettings,
        hidePatientNumber,
        isAllowedToAddUHID,
        isAllowedToAddImportId,
        hideRelationShipDetails,
        showAdminFields,
        compactEssentials,
        overrides: initialVisibilityConfig,
      }),
    // Only the inputs the rules actually read are listed, so typing in unrelated fields
    // does not recompute visibility.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      attendantsCount,
      relatedPatientKey,
      hasMobile,
      hasEmail,
      makeAnonymousPatient,
      isForcingAnonymousPatient,
      moveEmailToMoreSettings,
      hidePatientNumber,
      isAllowedToAddUHID,
      isAllowedToAddImportId,
      hideRelationShipDetails,
      showAdminFields,
      compactEssentials,
      overridesKey,
    ],
  )
}

export default useFormFieldVisibility
